import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Link,
  Stepper,
  Step,
  StepLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sendRecoveryEmail, resetPasswordWithCode } from '../services/AuthService';

type Mode = 'id' | 'password';

const ID_STEPS = ['이메일 입력', '완료'];
const PW_STEPS = ['이메일 입력', '인증 코드 확인', '비밀번호 재설정'];

const ForgotPasswordScreen: React.FC = () => {
  const navigate = useNavigate();
  const { guestToken } = useAuth();
  const [mode, setMode] = useState<Mode>('id');
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const steps = mode === 'id' ? ID_STEPS : PW_STEPS;

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: Mode | null) => {
    if (newMode) {
      setMode(newMode);
      setActiveStep(0);
      setError('');
    }
  };

  // Step 1: 이메일로 복구 메일 발송
  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      setIsLoading(true);
      setError('');
      await sendRecoveryEmail(guestToken || '', email);
      setActiveStep(1);
    } catch (err: any) {
      setError(err.message || '이메일 전송에 실패했습니다. 가입된 이메일인지 확인해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  // 비밀번호 찾기 Step 2: 인증 코드 확인 후 다음 단계
  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) return;
    setError('');
    setActiveStep(2);
  };

  // 비밀번호 찾기 Step 3: 새 비밀번호로 재설정
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    if (newPassword.length < 8) {
      setError('비밀번호는 8자 이상이어야 합니다.');
      return;
    }
    try {
      setIsLoading(true);
      setError('');
      await resetPasswordWithCode(guestToken || '', email, code, newPassword);
      setActiveStep(3);
    } catch (err: any) {
      setError(err.message || '비밀번호 재설정에 실패했습니다. 인증 코드를 다시 확인해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  const isDone = (mode === 'id' && activeStep === 1) || (mode === 'password' && activeStep === 3);

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 3 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" align="center" gutterBottom>
            아이디/비밀번호 찾기
          </Typography>

          {!isDone && (
            <>
              {/* 모드 선택 탭 */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={handleModeChange}
                  size="small"
                >
                  <ToggleButton value="id" sx={{ px: 4 }}>아이디 찾기</ToggleButton>
                  <ToggleButton value="password" sx={{ px: 4 }}>비밀번호 찾기</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map(label => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Step 1: 이메일 입력 (아이디/비밀번호 공통) */}
          {activeStep === 0 && (
            <form onSubmit={handleSendEmail}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {mode === 'id'
                  ? '가입 시 등록한 이메일 주소를 입력하시면 아이디를 보내드립니다.'
                  : '가입 시 등록한 이메일 주소를 입력하시면 비밀번호 재설정 코드를 보내드립니다.'}
              </Typography>
              <TextField
                fullWidth
                label="이메일"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                margin="normal"
                required
                autoFocus
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading}
                sx={{ mt: 3 }}
              >
                {isLoading ? '전송 중...' : '이메일 발송'}
              </Button>
            </form>
          )}

          {/* 아이디 찾기 완료 */}
          {mode === 'id' && activeStep === 1 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>이메일을 발송했습니다</Typography>
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <strong>{email}</strong>로 아이디와 비밀번호 재설정 코드를 발송했습니다.
                이메일을 확인해주세요.
              </Alert>
              <Button variant="contained" fullWidth size="large" onClick={() => navigate('/login')} sx={{ mb: 1 }}>
                로그인 하러 가기
              </Button>
              <Button variant="outlined" fullWidth size="large" onClick={() => { setMode('password'); setActiveStep(1); }}>
                비밀번호도 재설정하기
              </Button>
            </Box>
          )}

          {/* 비밀번호 찾기 Step 2: 인증 코드 입력 */}
          {mode === 'password' && activeStep === 1 && (
            <form onSubmit={handleVerifyCode}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>{email}</strong>로 아이디와 인증 코드를 발송했습니다.
              </Alert>
              <TextField
                fullWidth
                label="인증 코드"
                value={code}
                onChange={e => setCode(e.target.value)}
                margin="normal"
                required
                autoFocus
                inputProps={{ maxLength: 10 }}
                placeholder="이메일로 받은 6자리 코드를 입력하세요"
              />
              <Button type="submit" variant="contained" fullWidth size="large" disabled={!code} sx={{ mt: 3 }}>
                다음
              </Button>
              <Button variant="text" fullWidth size="small" onClick={handleSendEmail} disabled={isLoading} sx={{ mt: 1 }}>
                {isLoading ? '재전송 중...' : '코드 재발송'}
              </Button>
            </form>
          )}

          {/* 비밀번호 찾기 Step 3: 새 비밀번호 입력 */}
          {mode === 'password' && activeStep === 2 && (
            <form onSubmit={handleResetPassword}>
              <TextField
                fullWidth
                label="새 비밀번호"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                margin="normal"
                required
                autoFocus
                helperText="8자 이상 입력해주세요"
              />
              <TextField
                fullWidth
                label="비밀번호 확인"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                margin="normal"
                required
              />
              <Button type="submit" variant="contained" fullWidth size="large" disabled={isLoading} sx={{ mt: 3 }}>
                {isLoading ? '변경 중...' : '비밀번호 변경'}
              </Button>
            </form>
          )}

          {/* 비밀번호 찾기 완료 */}
          {mode === 'password' && activeStep === 3 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>비밀번호가 변경되었습니다</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                새 비밀번호로 로그인해주세요.
              </Typography>
              <Button variant="contained" fullWidth size="large" onClick={() => navigate('/login')}>
                로그인 하러 가기
              </Button>
            </Box>
          )}

          {!isDone && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link component="button" variant="body2" onClick={() => navigate('/login')}>
                로그인으로 돌아가기
              </Link>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPasswordScreen;
